/* Colour scheme.

   Three states: 'light' (the default), 'dark', and 'system' (follow the OS).

   Light is the default on purpose. The Claude Design canvas this is built from
   is a light design — dark only appears there on surfaces that are dark by
   nature (the atlas, the trial editor, the video player). Defaulting to the OS
   meant a reader on a dark-mode machine never saw the design as drawn. Dark is
   a real, complete palette and remains one click away; it is just not what the
   page opens as. Change DEFAULT below to 'system' to reverse that.

   The dark palette is the design's own, from the Claude Design canvas. It was
   unusable while Canvas was the target, because Canvas strips the stylesheet that carries it.
*/

import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const KEY = 'js1.theme';
const DEFAULT = 'light';
const ORDER = ['light', 'dark', 'system'];
const LABEL = { light: 'Light', dark: 'Dark', system: 'Auto' };

const ThemeContext = createContext(null);

const read = () => {
  try {
    const v = localStorage.getItem(KEY);
    return ORDER.includes(v) ? v : DEFAULT;
  } catch { return DEFAULT; }
};

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(read);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'system') root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', theme);
  }, [theme]);

  const setTheme = useCallback(next => {
    setThemeState(next);
    try { localStorage.setItem(KEY, next); } catch { /* session only */ }
  }, []);

  const cycle = useCallback(() => {
    setTheme(ORDER[(ORDER.indexOf(read()) + 1) % ORDER.length]);
  }, [setTheme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, cycle, label: LABEL[theme] }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}
